/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {Account, CRUDPermissions} from '@ion/core/src/types/entities';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {MapEditIcon} from '@ion/dls/src/icons/MapEditIcon';
import {NotificationTimeIcon} from '@ion/dls/src/icons/NotificationTimeIcon';
import {SSOConfigIcon} from '@ion/dls/src/icons/SSOConfigIcon';
import {SystemDeleteIcon} from '@ion/dls/src/icons/SystemDeleteIcon';
import {ViewRecordIcon} from '@ion/dls/src/icons/ViewRecordIcon';
import {IconSize} from '@ion/dls/src/types/types';
import * as actions from './state';

export interface GroupRowEditorTypes {
  group: Account;
  showSSOConfig?: boolean;
  allowUpdateSSOConfig?: boolean;
  includeGlobalSearch?: boolean;
  accountsPermissions?: CRUDPermissions;
}

interface SSOMenuItemOptions {
  showSSOConfig: boolean;
  allowUpdateSSOConfig: boolean;
  isEditSSOConfig: boolean;
  accountsPermissions?: CRUDPermissions;
}

const canShowSSOMenuItem = ({
  showSSOConfig,
  allowUpdateSSOConfig,
  isEditSSOConfig,
  accountsPermissions
}: SSOMenuItemOptions): boolean => (
  showSSOConfig && !!(isEditSSOConfig
    ? allowUpdateSSOConfig && accountsPermissions?.hasReadPermission
    : accountsPermissions?.hasReadPermission && accountsPermissions?.hasUpdatePermission)
);

export const GroupsRowEditor = ({
  group,
  accountsPermissions,
  showSSOConfig = false,
  allowUpdateSSOConfig = false,
  includeGlobalSearch = false
}: GroupRowEditorTypes) => {
  const dispatch    = useDispatch();
  const cellMenuRef = useRef(null);
  const {
    hasReadPermission,
    hasUpdatePermission,
    hasDeletePermission
  } = accountsPermissions || {};

  const isEditSSOConfig       = !!group?.ssoConfig;
  const showSSOConfigMenuItem = canShowSSOMenuItem({
    showSSOConfig,
    allowUpdateSSOConfig,
    isEditSSOConfig,
    accountsPermissions
  });

  const closeMenu = useCallback(() => {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  }, []);

  const onAction = useCallback((type: string, viewMode: boolean) => {
    return function() {
      if (viewMode) {
        dispatch(actions[type]({
          account    : group,
          show       : true,
          isViewMode : viewMode
        }));
      } else {
        dispatch(actions[type](type === 'editAdminGroup' ? {account: group} : group));
      }
      closeMenu();
    };
  }, [group]);

  const onSSOConfig = useCallback(() => {
    closeMenu();
    if (isEditSSOConfig) {
      dispatch(actions.editSSOConfig(group));
    } else {
      dispatch(actions.addSSOConfig(group));
    }
  }, [group]);

  return (
    <>
      <CellMenu ref={cellMenuRef} menuItems={
        <div>
          <CellMenuItem icon={<ViewRecordIcon size={IconSize.medium} />}
            onClick={onAction('editAdminGroup', true)}
            disabled={!hasReadPermission}
            label={translate('admin-groups:grid.menu.view')} />
          <CellMenuItem icon={<MapEditIcon />}
            onClick={onAction('editAdminGroup', false)}
            disabled={!hasUpdatePermission}
            label={translate('admin-groups:grid.menu.edit')} />
          {showSSOConfigMenuItem && (
            <CellMenuItem icon={<SSOConfigIcon />}
              onClick={onSSOConfig}
              label={translate('admin-groups:grid.menu.ssoConfig')} />
          )}
          <CellMenuItem icon={<NotificationTimeIcon />}
            onClick={onAction('auditAdminGroup', false)}
            label={translate('admin-groups:grid.menu.auditLog')} />
          {!includeGlobalSearch && (
            <CellMenuItem icon={<SystemDeleteIcon />}
              onClick={onAction('deleteAdminGroup', false)}
              disabled={!hasDeletePermission}
              label={translate('admin-groups:grid.menu.delete')} />
          )}
        </div>
      } />
    </>
  );
};
